import { createContext, useCallback, useEffect, useMemo } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import type { JSX, ReactNode } from 'react';

import { EMPTY_ID } from '@e-socna/empty-client';

import { MissionPagesGuard } from '../../components/Access/MissionPagesGuard';
import { LoadingFailure } from '../../components/LoadingFailure';
import { PATH_NOT_FOUND } from '../../app/constants/paths.constant';
import {
  COLLABORATION_ID_PARAM,
  findMissionSpaceRedirection,
  isMissionSpace,
  MISSION_SPACE_PARAM,
  subMissionSpaceOf,
} from '../../app/utils/mission-space.util';
import { getLoadingFailureMessage } from '../../utils/loading-failure-message.util';
import { useIdParam } from '../../utils/use-id-param.util';
import { useFindOneMissionQuery } from '../../pages/Mission/hooks/useFindOneMissionQuery.hook';
import { useFindOneResolvableMissionTypeQuery } from '../../pages/ResolvableMissionType/hooks/useFindOneResolvableMissionTypeQuery.hook';

import { useNavigationContext } from '../NavigationContext/hooks/useNavigationContext.hook';
import { useSpaceEmitterColor } from '../UiSettingsContext/hooks/useSpaceEmitterColor.hook';
import { SpaceLevel } from '../UiSettingsContext/enums/space-level.enum';
import { useFolderContext } from '../FolderContext/hooks/useFolderContext.hook';
import { MissionWarningsContextProvider } from '../MissionWarningsContext';
import { FileReadContextProvider } from '../FileReadContext';

import { MissionContextStatus } from './types/mission-context.type';
import type { MissionContextData } from './types/mission-context.type';

export { DummyMissionContextProvider } from './DummyMissionContextProvider';

const MissionContext = createContext<MissionContextData | null>(null);

interface Props {
  readonly children?: ReactNode;
  // A print page: the printer clicks its controls, no notification may cover them.
  readonly isInPrintMode?: boolean;
}

const MissionContextProvider = ({
  children,
  isInPrintMode = false,
}: Readonly<Props>): JSX.Element => {
  const { setMissionSpaceInfo } = useNavigationContext();

  // Get folder from parent FolderContext (no need to refetch)
  const { folder, folderId } = useFolderContext();

  const missionId = useIdParam('missionId');

  // Fetch mission
  const {
    data: missionQueryData,
    isLoading: isMissionLoading,
    isError: isMissionError,
    error: missionError,
    refetch: refetchMission,
  } = useFindOneMissionQuery(missionId);
  const mission = missionQueryData?.findOneMission;

  // Fetch resolvableMissionType
  const resolvableMissionTypeId = mission?.typeId;
  const {
    data: resolvableMissionTypeData,
    isLoading: isTypeLoading,
    isError: isTypeError,
    error: typeError,
    refetch: refetchType,
  } = useFindOneResolvableMissionTypeQuery(resolvableMissionTypeId ?? EMPTY_ID);
  const resolvableMissionType =
    resolvableMissionTypeData?.findOneResolvableMissionType;

  // Update NavigationContext with mission info
  useEffect(() => {
    if (!mission) {
      return;
    }

    setMissionSpaceInfo(mission);
  }, [mission, setMissionSpaceInfo]);

  useSpaceEmitterColor(SpaceLevel.Mission, mission?.consolidation);

  const isLoading = isMissionLoading || (mission?.typeId && isTypeLoading);
  const isError = isMissionError || isTypeError;

  const retry = useCallback(() => {
    void refetchMission();
    void refetchType();
  }, [refetchMission, refetchType]);

  const failureMessage = getLoadingFailureMessage({
    isLoading: !!isLoading,
    isError,
    error: missionError ?? typeError,
    isFound: !!mission,
    subject: 'la mission',
    notFoundMessage: 'Cette mission est introuvable.',
  });

  const contextValue = useMemo<MissionContextData>(() => {
    if (isLoading) {
      return {
        status: MissionContextStatus.Loading,
        missionId,
        folderId,
        resolvableMissionTypeId: undefined,
        mission: undefined,
        folder,
        resolvableMissionType: undefined,
      };
    }

    if (isError || !mission) {
      return {
        status: MissionContextStatus.Error,
        missionId,
        folderId,
        resolvableMissionTypeId: undefined,
        mission: undefined,
        folder,
        resolvableMissionType: undefined,
      };
    }

    if (!folder || !folderId) {
      return {
        status: MissionContextStatus.Loading,
        missionId,
        folderId,
        resolvableMissionTypeId: undefined,
        mission: undefined,
        folder,
        resolvableMissionType: undefined,
      };
    }

    return {
      status: MissionContextStatus.Ready,
      missionId,
      folderId,
      resolvableMissionTypeId,
      mission,
      folder,
      resolvableMissionType,
      relatedBusinessEntities: {
        folder,
        mission,
        resolvableMissionType,
      },
    };
  }, [
    isLoading,
    isError,
    missionId,
    folderId,
    resolvableMissionTypeId,
    mission,
    folder,
    resolvableMissionType,
  ]);

  // A collaboration is served under "collaborations", a sub-mission nested in
  // its collaboration, any other mission under "missions"; the printer routes
  // have a fixed "missions" segment instead.
  const {
    [MISSION_SPACE_PARAM]: missionSpace,
    [COLLABORATION_ID_PARAM]: collaborationId,
  } = useParams();
  const { pathname, search, hash } = useLocation();

  if (missionSpace !== undefined && !isMissionSpace(missionSpace)) {
    return <Navigate replace to={PATH_NOT_FOUND} />;
  }

  const currentSpace = collaborationId
    ? subMissionSpaceOf(collaborationId)
    : missionSpace;
  const missionSpaceRedirection =
    mission && currentSpace
      ? findMissionSpaceRedirection({
          pathname,
          currentSpace,
          kind: mission.kind,
          compositeMissionId: mission.compositeMissionId,
        })
      : null;

  if (missionSpaceRedirection) {
    return (
      <Navigate replace to={`${missionSpaceRedirection}${search}${hash}`} />
    );
  }

  return (
    <MissionContext.Provider value={contextValue}>
      <FileReadContextProvider
        value={missionId ? `mission:${missionId}` : undefined}
      >
        <MissionWarningsContextProvider isSilent={isInPrintMode}>
          {failureMessage ? (
            <LoadingFailure failure={failureMessage} onRetry={retry} />
          ) : (
            (children ?? <MissionPagesGuard />)
          )}
        </MissionWarningsContextProvider>
      </FileReadContextProvider>
    </MissionContext.Provider>
  );
};

export { MissionContext, MissionContextProvider };
